﻿import {translation} from "@2d-front-monorepo/core";
import {Message, PopOver} from "@2d-front-monorepo/design-library";
import {
	AddressComponent,
	Attachments,
	Drivers,
	ExpiredAttachmentsComponent,
	formatDate,
	formatTime,
	GroupedPermit,
	InstallationByHostData, Permits,
	PermitStatusComponent,
	PermitStatusType,
	Residencies,
	Tariffs,
	Vehicles,
	Zones
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import React, {JSX, ReactNode, useMemo} from "react";
import {Link} from "react-router";
import {API_ENDPOINT} from "../../../api-endpoint";
import {FilteredItemsList} from "../../../components/Filters/FilteredItemsList";
import {FilteredItemsProvider} from "../../../components/Filters/FilteredItemsProvider";
import {PermitsFilterComponent} from "../../../components/Filters/PermitsFilterComponent";
import Loader from "../../../components/Loader";
import MaterialIcon from "../../../components/MaterialIcon";
import {PermitsState} from "../../../contexts/PermitsContext";
import Registrations, {DefaultPermitProps} from "../../../customers/Registrations";
import {getGlobalContext} from "../../../hooks/getGlobalContext";
import getResidencyHiddenFields from "../../../hooks/getResidencyHiddenFields";
import {formatRoute, PrivateRoutes} from "../../../routes";
import {BillComponent} from "./BillComponent";
import PermitStatusReason from "./PermitStatusReason";
import {PermitTotalComponent} from "./PermitTotalComponent";
import {RenewComponent} from "./RenewComponent";
import {RenewGroupComponent} from "./RenewGroupComponent";
import {SentToReviewComponent} from "./SentToReviewComponent";

type PermitProps = DefaultPermitProps & {
	menu?: ReactNode;
	footer?: ReactNode;
}

const filesPrefix = API_ENDPOINT.fileDownload;

function Permit(props: PermitProps): JSX.Element {
	const {group, driver, residency, vehicles, tariff, zone, menu, footer} = props;
	const permit = group.permit;
	const {type: statusType} = permit.status;

	const hasChangeResidency = residency?.changed != null;
	const {installation} = getGlobalContext();
	const hasChangeVehicle = useMemo(() => vehicles.some(x => x.changed != null), [vehicles]);
	const expired = useMemo(() => Attachments.findExpired([driver?.original.entity, residency?.original.entity, ...(vehicles.map(x => x.original.entity))]), [driver?.original.entity, residency?.original.entity, vehicles]);
	const canUpdateExpired = statusType === PermitStatusType.RequestMoreInformation && expired.length > 0;

	const grouped = group.count > 1;
	const isMulti = Permits.isMultiOperation(permit);
	const klass = useMemo(() => "card" + (grouped || isMulti ? " card--stacked" : ""), [isMulti]);

	const notes = grouped ? Permits.getNotes(permit) : undefined;
	const hiddenResidencyFields = getResidencyHiddenFields(installation, residency, permit.category);
	const vehiclesLength = vehicles.length;
	const lastColumn = residency != null && vehiclesLength > 0 ? "4" : "2";

	//cuando por configuración no se agrupa, grouped siempre devuelve 1, por lo que no sirve para saber si están agrupados/mostrar card.
	return (
		<div className="card--stacked-wrapper">
			<section className={klass}>
				<header className="layout horizontal aligned middle" id={"p_" + permit.id}>
					<div className="flex">
						<div className="grid">
							<dl className="permit__date col-1of2">
								<dt>{translation(PermitTranslations.start_date)}</dt>
								<dd>
									<span>{formatDate(group.startDate.toDateOnly())}</span>
									<span>{formatTime(group.startDate.toTimeOnly())}</span>
								</dd>
							</dl>
							<dl className="permit__date col-1of2">
								<dt>{translation(PermitTranslations.end_date)}</dt>
								<dd>
									<span>{group.endDate && formatDate(group.endDate.toDateOnly())}</span>
									<span>{group.endDate && formatTime(group.endDate.toTimeOnly())}</span>
								</dd>
							</dl>
						</div>

						{notes != null && <small className="notes">{notes}</small>}
					</div>

					{isMulti
    ? <div className="permit__header-actions"><Loader small={true}/></div>
    : <div className="permit__header-actions">
        <PermitStatusComponent permit={permit}/>
        <div><PopOver>{menu}</PopOver></div>
      </div>}
				</header>
				<PermitStatusReason permit={permit}/>
				<hr className="no-margin"/>
				<div className="grid padded">
					<dl className="title-desc col-1of4">
						<dt>{translation(PermitTranslations.zone)}</dt>
						<dd>{zone.name || zone.id}</dd>
					</dl>

					<dl className="title-desc col-1of4">
						<dt>{translation(PermitTranslations.permit_tariff)}</dt>
						<dd>{tariff.title}</dd>
					</dl>

					{residency && <dl className={"title-desc col-1of" + lastColumn}>
                        <dt>{translation(PermitTranslations.address)}</dt>
                        <dd className="layout">
							{hasChangeResidency &&
                                <MaterialIcon icon="swap_horizontal_circle" className="entity-change--icon"
                                              title={translation(PermitTranslations.change_in_progress)}/>}
                            <div className="flex">
                                <AddressComponent value={residency.original.entity}
                                                  hiddenEntityProperties={hiddenResidencyFields}/>
                            </div>
                        </dd>
                    </dl>}

					{vehiclesLength > 0 &&
                        <dl className={"title-desc col-1of" + lastColumn}>
                            <dt>{vehiclesLength == 1 ? translation(PermitTranslations.vehicles) : `${translation(PermitTranslations.vehicles)}  [${vehiclesLength}]`}</dt>
                            <dd className="layout">
								{hasChangeVehicle &&
                                    <MaterialIcon icon="swap_horizontal_circle" className="entity-change--icon"
                                                  title={translation(PermitTranslations.change_in_progress)}/>}
                                <div className="flex tabular-nums">{vehicles.map(x => x.original.entity.plate).join(", ")}</div>
                            </dd>
                        </dl>}
					{footer}
					{canUpdateExpired &&
                        <ExpiredAttachmentsComponent
                            attachments={expired}
                            linkPrefix={filesPrefix} className="col-full">
                            <div>
                                <Link
                                    className="entity-documentation-update form__button form__button--primary form__button--small"
                                    to={formatRoute(PrivateRoutes.permitDocumentationUpdate, permit.id)}>{translation(PermitTranslations.documentation__update__button)}</Link>
                            </div>
                        </ExpiredAttachmentsComponent>
					}
				</div>
				{grouped ? undefined : <SentToReviewComponent permit={permit} expired={expired}/>}
				{grouped ? <RenewGroupComponent permits={group.permits}/> : <RenewComponent permit={permit}/>}
				{grouped ? undefined : <BillComponent permit={permit}/>}
				{grouped ? undefined : <PermitTotalComponent permit={permit}/>}
			</section>
		</div>);
}

export default function PermitListComponent(props: PermitsState & {
	installation: InstallationByHostData,
	grouped: GroupedPermit[],
}): JSX.Element {
	const MenuComponent = Registrations.getPermitMenu();
	const FooterComponent = Registrations.getPermitFooter();
	const {grouped, installation} = props;

	if (grouped.length === 0) {
		return <Message title={{text: translation(PermitTranslations.summary_page__no_permits)}} type="information"/>;
	}

	return <div className="layout vertical gap">
		<FilteredItemsProvider items={grouped}>
			<PermitsFilterComponent/>
			<FilteredItemsList
				keyExtractor={(group: GroupedPermit) => group.permit.id}
				renderItem={(group: GroupedPermit) => {
					const permit = group.permit;
					const tariff = Tariffs.get(installation, permit.tariffId);
					const zone = Zones.get(installation, permit.parkingLocation.zoneId);
					const driver = Drivers.getChanged(props, permit.driver);
					const residency = Residencies.getChanged(props, permit.residency);
					const vehicles = [...new Map(group.permits
						.map(p => Vehicles.getChanged(props, p.vehicles))
						.flat()
						.map(v => [v.original.entity.plate, v])).values()];

					return <Permit key={permit.id}
					               group={group}
					               permit={permit}
					               permits={props}
					               driver={driver}
					               residency={residency}
					               vehicles={vehicles}
					               zone={zone}
					               tariff={tariff}
					               now={installation.now}
					               footer={<FooterComponent group={group}
					                                        permit={permit}
					                                        permits={props}
					                                        driver={driver}
					                                        residency={residency}
					                                        vehicles={vehicles}
					                                        zone={zone}
					                                        tariff={tariff}
					                                        now={installation.now}/>}
					               menu={<MenuComponent group={group}
					                                    permit={permit}
					                                    permits={props}
					                                    driver={driver}
					                                    residency={residency}
					                                    vehicles={vehicles}
					                                    zone={zone}
					                                    tariff={tariff}
					                                    now={installation.now}/>}
					/>
				}}/>
		</FilteredItemsProvider>
	</div>;
}
